import { BillDocumentType } from "@btnet/services-btnet/handlers/models/billDocumentType";
import { PurchaseOrderPaymentStatus } from "@btnet/services-btnet/handlers/models/purchaseOrderPaymentStatus";
import type { PurchaseOrderPaymentStatusColumn } from "@btnet/services-btnet/handlers/models/purchaseOrderPaymentStatusColumn";

import { type IBaseEntity } from "types/apiResponse/apiResponse";

/**
 * A vendor credit has no lien waiver UI, so it can never have a lien waiver applied. A null/unknown
 * document type is a legacy bill and is treated as a regular bill (not a vendor credit).
 */
export const isVendorCredit = (status: PurchaseOrderPaymentStatusColumn | undefined): boolean =>
    status?.documentType === BillDocumentType.VendorCredit;

export interface IBillRowSelection extends IBaseEntity {
    purchaseOrderPaymentStatus?: PurchaseOrderPaymentStatusColumn;
}

// Returns the shared document type of a selection, or undefined when the selection is empty or
// mixed (so callers fall back to bill wording). Reads each row's purchaseOrderPaymentStatus
// documentType; a null/unknown type is treated as a regular bill.
export const getSelectionDocType = (
    rows: Pick<IBillRowSelection, "purchaseOrderPaymentStatus">[],
): BillDocumentType | undefined => {
    if (rows.length === 0) {
        return undefined;
    }
    const docTypes = rows.map(
        (row) => row.purchaseOrderPaymentStatus?.documentType ?? BillDocumentType.Bill,
    );
    const [first] = docTypes;
    return docTypes.every((docType) => docType === first) ? first : undefined;
};

export const canMarkReadyForPayment = (
    status: PurchaseOrderPaymentStatusColumn | undefined,
): boolean => {
    const paymentStatus = status?.paymentStatus;
    if (paymentStatus == null) {
        return false;
    }
    switch (paymentStatus) {
        case PurchaseOrderPaymentStatus.ReadyForPayment:
        case PurchaseOrderPaymentStatus.Paid:
        case PurchaseOrderPaymentStatus.UnderSubReview:
        case PurchaseOrderPaymentStatus.OnlinePaymentProcessing:
        case PurchaseOrderPaymentStatus.Void:
        case PurchaseOrderPaymentStatus.InReview:
            return false;
        default:
            return true;
    }
};

export const canRecordOfflinePayment = (
    status: PurchaseOrderPaymentStatusColumn | undefined,
): boolean => {
    const paymentStatus = status?.paymentStatus;
    if (paymentStatus == null) {
        return false;
    }
    switch (paymentStatus) {
        case PurchaseOrderPaymentStatus.Open:
        case PurchaseOrderPaymentStatus.InReview:
        case PurchaseOrderPaymentStatus.Paid:
        case PurchaseOrderPaymentStatus.UnderSubReview:
        case PurchaseOrderPaymentStatus.OnlinePaymentProcessing:
        case PurchaseOrderPaymentStatus.Void:
        case PurchaseOrderPaymentStatus.Draft:
            return false;
        default:
            return true;
    }
};

export const canApplyLienWaiver = (
    status: PurchaseOrderPaymentStatusColumn | undefined,
): boolean => {
    const paymentStatus = status?.paymentStatus;
    if (paymentStatus == null) {
        return false;
    }
    switch (paymentStatus) {
        case PurchaseOrderPaymentStatus.Open:
        case PurchaseOrderPaymentStatus.InReview:
        case PurchaseOrderPaymentStatus.ReadyForPayment:
            return true;
        default:
            return false;
    }
};
